import React, { useMemo } from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { BillingDocumentAction } from "actions";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { BillingDocumentForm } from "components/Billing/BillingDocument/Form";
import { ValidationSchema } from "components/Billing/BillingDocument/validationSchema";
import { Button, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import { Row } from "components/Layout";
import { XeroDeepLink } from "components/Link/XeroDeepLink";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";

import { AuditLogTypes } from "constants/auditLog";
import { BillingDocumentType } from "constants/billingDocuments";
import { ApiModel } from "constants/loading";
import { ModalAdapterProps, ModalTypes } from "constants/navigation";

import { deepObjectChanges } from "lib/compare";

import {
  getBillingDocumentById,
  getBillingSettingsByBillingRunId,
  getIsReadOnlyByBillingDocumentId,
} from "selectors";

import { useHasBillingRunPermission, useModalAdapter } from "hooks";

interface BillingDocumentModalParams {
  id: string;
  saleId?: string | null;
}

interface BillingDocumentModalProps {
  billingDocumentId: string;
  onClose: () => void;
  returnTo?: string;
  saleId?: string | null;
}

interface FooterProps {
  billingDocumentId: string;
  onClose: () => void;
}

type BillingDocumentFormValues = BillingDocument & {
  detailsText?: string | null;
};

type BillingDocumentPatch = Partial<BillingDocument> & {
  id: string;
};

export function BillingDocumentModalAdapter(
  props: ModalAdapterProps,
): React.JSX.Element | null {
  const [hashParams, returnTo, onClose] =
    useModalAdapter<BillingDocumentModalParams>(
      ModalTypes.EditBillingDocument,
      props,
    );

  const { id: billingDocumentId, saleId } = hashParams;

  const hasBillingRunFeaturePermission = useHasBillingRunPermission();

  if (!hasBillingRunFeaturePermission) {
    return null;
  }

  return (
    <WaitForSync
      requiredData={[ApiModel.BILLING_RUNS, ApiModel.BILLING_DOCUMENTS]}
    >
      <BillingDocumentModal
        billingDocumentId={billingDocumentId}
        onClose={onClose}
        returnTo={returnTo}
        saleId={saleId}
      />
    </WaitForSync>
  );
}

export function Footer(props: FooterProps): React.JSX.Element {
  const { onClose, billingDocumentId } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);

  // We have no deeper permissions on a billing document - it's all billing run driven - so
  // if they're here, we're letting them update it.
  const isReadOnly = useSelector(
    getIsReadOnlyByBillingDocumentId(billingDocumentId),
  ) as boolean;

  useSubmitHandler(false, setIsSubmitEnabled);

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      <Button type="submit" disabled={!isSubmitEnabled || isReadOnly}>
        Save
      </Button>
    </>
  );
}

export function BillingDocumentModal(
  props: BillingDocumentModalProps,
): React.JSX.Element {
  const { billingDocumentId, onClose, saleId } = props;
  const dispatch = useDispatch();
  const billingDocument = useSelector(
    getBillingDocumentById(billingDocumentId),
  ) as BillingDocument;
  const billingSettings = useSelector(
    getBillingSettingsByBillingRunId(billingDocument.billingRunId),
  ) as BillingSettings | null;

  const documentType = billingDocument.type;

  const defaultDetailsText = useMemo(() => {
    if (documentType === BillingDocumentType.RCTI) {
      return billingSettings?.rctiSettings?.detailsText;
    } else {
      return billingSettings?.taxInvoiceSettings?.detailsText;
    }
  }, [documentType, billingSettings]);

  const initialValues = useMemo<BillingDocumentFormValues>(
    () => ({
      ...billingDocument,
      detailsText:
        typeof billingDocument.detailsText === "string"
          ? billingDocument.detailsText
          : defaultDetailsText,
    }),
    [billingDocument, defaultDetailsText],
  );

  const prefillApplied = useMemo(
    () => initialValues.detailsText === defaultDetailsText,
    [defaultDetailsText, initialValues],
  );

  function onSubmit(values: BillingDocumentFormValues): void {
    const patch: BillingDocumentPatch = {
      ...deepObjectChanges(initialValues, values),
      id: billingDocumentId,
    };

    dispatch(
      BillingDocumentAction.update(patch, {
        changeReason: "Updated from Edit Billing Document modal",
      }),
    );

    onClose();
  }

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>
        <Row>
          <AuditLogLink
            auditLogType={AuditLogTypes.BILLING_DOCUMENT}
            dataId={billingDocumentId}
            returnTo={window.location.hash}
          />
          &nbsp;Edit Billing Document
          <AdminLink model="billingdocument" cbid={billingDocumentId} />
          <XeroDeepLink billingDocumentId={billingDocumentId} />
        </Row>
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
      >
        <Form data-tour="billing-document-form">
          <DialogContent dividers>
            <BillingDocumentForm
              billingDocumentId={billingDocumentId}
              saleId={saleId}
              prefillApplied={prefillApplied}
            />
          </DialogContent>
          <DialogActions>
            <Footer billingDocumentId={billingDocumentId} onClose={onClose} />
          </DialogActions>
        </Form>
      </Formik>
    </ZoomyDialog>
  );
}
